import React from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DOCUMENT_SLUG_TO_LABEL } from '../utils/leadRoutes';
import { Lead, contactDisplayName } from '../types';
import { parseOfferDocumentDraft } from '../features/leads/utils/offerDocumentDraft';
import {
  projectDetailPath,
  projectDocumentEditorPathFromLabel,
  projectDocumentPathFromSlug,
  projectsListPath,
} from '../utils/projectRoutes';
import { LeadProdukteTab, type LeadProdukteTabOfferingSection } from '../features/leads/components/LeadProdukteTab';
import { OfferDocumentEditor } from '../features/leads/components/OfferDocumentEditor';
import { LeadDocumentsTab } from '../features/projects/components/LeadDocumentsTab';
import {
  LeadFileDropLayer,
  type LeadFileDropFeedback,
} from '../features/projects/components/LeadFileDropLayer';
import { useWindowFileDrop } from '../hooks/useWindowFileDrop';
import { resolveLastUsedOfferingId, sortOfferingsForDisplay } from '../utils/offeringSelection';
import { DEFAULT_OFFERING_TITLE } from '../utils/offeringLabels';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNavigateBack } from '../hooks/useNavigateBack';
import { ArrowLeft } from 'lucide-react';
import { PageLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { useCompanyTimezoneState } from '../hooks/useCompanyTimezone';
import { mapApiLeadToInboxLeadOption, type LeadOption } from '../features/inbox/inboxShared';
import { useCapabilities } from '../hooks/useCapabilities';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { isDocumentTypeAvailable } from '../lib/documentTypeModules';
import { LEAD_DOCUMENT_TYPES } from '../features/leads/components/detail/leadActivityHelpers';
import { LeadDetailMobileHero } from '../features/leads/components/detail/LeadDetailMobileHero';
import { LeadNoteComposer } from '../features/leads/components/detail/LeadNoteComposer';
import { LeadActivityLists } from '../features/leads/components/detail/LeadActivityLists';
import { LeadHistoryFeed } from '../features/leads/components/detail/LeadHistoryFeed';
import { LeadDetailModals } from '../features/leads/components/detail/LeadDetailModals';
import { LeadDetailMobileActions } from '../features/leads/components/detail/LeadDetailMobileActions';
import { LeadIncomingInvoicesCard } from '../features/leads/components/detail/LeadIncomingInvoicesCard';
import { LeadWorkReportsCard } from '../features/leads/components/detail/LeadWorkReportsCard';
import { LeadInfoColumn } from '../features/leads/components/detail/LeadInfoColumn';
import { MergeProjectModal } from '../features/leads/components/MergeProjectModal';
import { LeadDetailDesktopHeader } from '../features/leads/components/detail/LeadDetailDesktopHeader';
import { useLeadDetailLead } from '../features/leads/components/detail/useLeadDetailLead';
import { useLeadDocuments } from '../features/leads/components/detail/useLeadDocuments';
import { useLeadActivities } from '../features/leads/components/detail/useLeadActivities';
import { useLeadNotes } from '../features/leads/components/detail/useLeadNotes';
import { useLeadHistoryEntries, type HistoryFilter } from '../features/leads/components/detail/useLeadHistoryEntries';
import { useLeadProjectChats } from '../features/leads/components/detail/useLeadProjectChats';
import { useLeadTasks } from '../features/leads/components/detail/useLeadTasks';
import { useInvoiceSourceChoice } from '../features/workReports/components/InvoiceSourceChoice';

export type NoteHistoryEntry = {
  id: number | string;
  lead_id: number | string;
  saved_at: string;
  snippet: string | null;
  author_label?: string;
};

export type LeadEmailEntry = {
  id: string;
  account_id?: number | string | null;
  external_message_id?: string | null;
  thread_id?: string | null;
  lead_id?: string | number | null;
  attachments_json?: string | unknown[] | null;
  from_addr: string;
  to_addr: string;
  subject: string;
  date_at: string | null;
  snippet: string;
  body_text?: string | null;
  body_html?: string | null;
  /** Body is longer than the snippet; the list omits the body and loads it on expand. */
  has_more_body?: boolean | null;
  created_at: string;
};

export type LeadActivity = {
  id: string;
  lead_id: string;
  title: string;
  activity_type: string;
  checklist_id: string | null;
  due_at: string | null;
  end_at?: string | null;
  priority?: string | null;
  notes?: string | null;
  done: boolean;
  created_at: string;
  created_by?: string | null;
  created_by_label?: string | null;
  scheduling_status?: string;
  site_address?: string | null;
  assignee_users?: { user_id: string; display_name: string | null; email: string | null }[];
  assignee_employees?: { id: string; first_name: string; last_name: string; color_idx?: number; position?: string }[];
  series_id?: string | null;
  recurrence?: import('../utils/activityRecurrence').ActivityRecurrence | null;
  recurrence_label?: string | null;
  occurrence_count?: number;
  done_count?: number;
  occurrence_ids?: string[];
  is_series?: boolean;
  original_start_at?: string | null;
  series_exception?: boolean;
};

/** Seitengeruest (App-Sidebar + Topbar) ? geteilt von Lade-, Fehler- und Inhaltszustand. */
function LeadDetailShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
      <AppSidebar active="leads" />
      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden bg-app-surface-muted/50 pb-16 md:pb-0">
        <AppTopBar title="Projekt" />
        {children}
      </main>
    </div>
  );
}

export function LeadDetailPage() {
  const { timeZone, ready: timeZoneReady } = useCompanyTimezoneState();
  const location = useLocation();
  const { id, offeringId: offeringIdParam, docType } = useParams<{
    id: string;
    offeringId?: string;
    docType?: string;
  }>();
  const navigate = useNavigate();
  const goBack = useNavigateBack(projectsListPath());
  const { can } = useCapabilities();
  const canViewInbox = can('web.posteingang.view');
  const canEditProjects = can('web.projekte.edit');
  const { loaded: modulesLoaded, isEnabled, modules } = useModuleAccess();
  /** Rechnungsdokumente, Controlling und E-Mail-Verlauf brauchen ihr Modul; vor dem Laden gilt es als aus. */
  const rechnungenEnabled = modulesLoaded && isEnabled('rechnungen');
  const emailsEnabled = modulesLoaded && isEnabled('posteingang');
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [searchParams] = useSearchParams();

  const leadApi = useLeadDetailLead(id);
  const { lead, isLoading, setLead, reloadLead, leadAssigneeUserIds } = leadApi;

  const docs = useLeadDocuments(lead?.id);
  const activitiesApi = useLeadActivities(lead, { timeZone, timeZoneReady, leadAssigneeUserIds });

  const [mobileNoteContent, setMobileNoteContent] = React.useState('');
  const notesApi = useLeadNotes(lead, {
    reloadLead,
    onSaved: () => setMobileNoteContent(''),
    isSaving: leadApi.isSaving,
    setIsSaving: leadApi.setIsSaving,
  });

  const [showCreateDocumentModal, setShowCreateDocumentModal] = React.useState(false);
  const [pendingDocumentTypeLabel, setPendingDocumentTypeLabel] = React.useState<
    (typeof LEAD_DOCUMENT_TYPES)[number] | null
  >(null);
  const [showOfferingPickerModal, setShowOfferingPickerModal] = React.useState(false);
  /** Angebots-Auswahl kam aus ?Neues Dokument erstellen? ? Zur?ck ?ffnet wieder den Dokumenttyp-Dialog. */
  const [offeringPickerFromCreateDoc, setOfferingPickerFromCreateDoc] = React.useState(false);
  const [editorLead, setEditorLead] = React.useState<Lead | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [isDeletingProject, setIsDeletingProject] = React.useState(false);
  const [deleteProjectError, setDeleteProjectError] = React.useState<string | null>(null);

  /** Derived from :docType URL param ? editor is open whenever the param is present. */
  const documentTypeLabel = React.useMemo<(typeof LEAD_DOCUMENT_TYPES)[number]>(() => {
    const label = docType ? DOCUMENT_SLUG_TO_LABEL[docType] : undefined;
    return (label as (typeof LEAD_DOCUMENT_TYPES)[number]) ?? 'Angebot';
  }, [docType]);
  const docTypeLabel = docType ? DOCUMENT_SLUG_TO_LABEL[docType] ?? '' : '';
  /** Belegtyp-URL ohne sein Modul (Angebote, Auftragsabwicklung, Rechnungen): kein Editor, zurück zum Projekt. */
  const invoiceDocTypeBlocked = Boolean(docTypeLabel) && modulesLoaded && !isDocumentTypeAvailable(docTypeLabel, modules);
  const offerEditorOpen = Boolean(docTypeLabel) && modulesLoaded && isDocumentTypeAvailable(docTypeLabel, modules);

  const leadDocumentTitle = React.useMemo(() => {
    if (!lead || !id) return null;
    const leadLabel = (lead.lead_title || lead.name || lead.display_id || '').trim() || null;
    if (!leadLabel) return null;
    const matchesRoute =
      String(lead.id) === String(id) ||
      (lead.display_id != null && String(lead.display_id) === String(id));
    if (!matchesRoute) return null;
    if (offerEditorOpen && docType && DOCUMENT_SLUG_TO_LABEL[docType]) {
      return `${DOCUMENT_SLUG_TO_LABEL[docType]} · ${leadLabel}`;
    }
    return leadLabel;
  }, [lead, id, offerEditorOpen, docType]);
  useDocumentTitle(leadDocumentTitle);

  /** Only set while the document editor is open (query `?offering=`). */
  const editorOfferingId = offerEditorOpen ? searchParams.get('offering') : null;

  /** Alle Angebote im Tab ?Angebot? (gestapelt). */
  const produkteTabSections = React.useMemo((): LeadProdukteTabOfferingSection[] => {
    if (!lead?.offerings?.length) return [];
    const lastUsedId = resolveLastUsedOfferingId(lead.offerings);
    return sortOfferingsForDisplay(lead.offerings, lead.primary_offering_id).map((o) => ({
      offeringId: o.id,
      offeringMeta: {
        title: o.title?.trim() || DEFAULT_OFFERING_TITLE,
        displayId: o.display_id ?? null,
        isLastUsed: o.id === lastUsedId,
        isPrimary: o.id === lead.primary_offering_id,
      },
      offerDiscount: parseOfferDocumentDraft(o.offer_document_draft)?.discount ?? null,
      offerVatMode: parseOfferDocumentDraft(o.offer_document_draft)?.vat_mode,
    }));
  }, [lead?.offerings, lead?.primary_offering_id]);

  const markOfferingLastUsed = React.useCallback(async (offeringId: string) => {
    try {
      const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/mark-last-used`, {
        method: 'POST',
      });
      if (res.ok) {
        const now = new Date().toISOString();
        setLead((prev) => {
          if (!prev?.offerings?.length) return prev;
          return {
            ...prev,
            offerings: prev.offerings.map((o) =>
              o.id === offeringId ? { ...o, last_used_at: now } : o,
            ),
          };
        });
      }
    } catch {
      /* ignore */
    }
  }, [setLead]);

  const openDocumentEditorWithSource = React.useCallback(
    (documentLabel: string, offeringId: string, workReportIds?: string[]) => {
      if (!lead) return;
      void markOfferingLastUsed(offeringId);
      let path = `${projectDocumentEditorPathFromLabel(lead, documentLabel, offeringId)}&neu=1`;
      const cancelInvoice = searchParams.get('cancelInvoice');
      if (cancelInvoice) path += `&cancelInvoice=${encodeURIComponent(cancelInvoice)}`;
      if (workReportIds?.length) path += `&workReports=${encodeURIComponent(workReportIds.join(','))}`;
      navigate(path);
      setShowOfferingPickerModal(false);
      setPendingDocumentTypeLabel(null);
    },
    [lead, navigate, markOfferingLastUsed, searchParams],
  );
  // Rechnung bei offenen Arbeitsberichten: erst fragen, ob aus Angebot oder aus Berichten.
  const { requestDocumentEditor, invoiceSourceModal } = useInvoiceSourceChoice(openDocumentEditorWithSource);
  const openDocumentEditor = React.useCallback(
    (documentLabel: (typeof LEAD_DOCUMENT_TYPES)[number], offeringId: string) => {
      setShowOfferingPickerModal(false);
      void requestDocumentEditor(documentLabel, offeringId);
    },
    [requestDocumentEditor],
  );

  const [historyContentTab, setHistoryContentTab] = React.useState<'notes' | 'activity' | 'files'>('notes');
  const [historyFilter, setHistoryFilter] = React.useState<HistoryFilter>('all');
  const { chats: leadChats, canViewChats } = useLeadProjectChats(lead?.id ? String(lead.id) : null);
  const leadTasks = useLeadTasks(lead?.id ? String(lead.id) : null);

  const [leadEmails, setLeadEmails] = React.useState<LeadEmailEntry[]>([]);
  const [leadEmailsTotal, setLeadEmailsTotal] = React.useState(0);
  const [leadEmailsPage, setLeadEmailsPage] = React.useState(1);
  const [leadEmailsLoadingMore, setLeadEmailsLoadingMore] = React.useState(false);
  const [leadsForEmailLink, setLeadsForEmailLink] = React.useState<LeadOption[]>([]);
  const [expandedEmailId, setExpandedEmailId] = React.useState<string | null>(null);
  const [emailLinkWarning, setEmailLinkWarning] = React.useState<string | null>(null);

  const parseLeadEmailsPayload = React.useCallback((payload: unknown): { items: LeadEmailEntry[]; total: number; page: number } => {
    if (Array.isArray(payload)) {
      return { items: payload as LeadEmailEntry[], total: payload.length, page: 1 };
    }
    if (payload && typeof payload === 'object') {
      const data = payload as { items?: unknown; total?: unknown; page?: unknown };
      const items = Array.isArray(data.items) ? (data.items as LeadEmailEntry[]) : [];
      return {
        items,
        total: Number(data.total ?? items.length),
        page: Number(data.page ?? 1),
      };
    }
    return { items: [], total: 0, page: 1 };
  }, []);

  const loadLeadEmails = React.useCallback((options?: { page?: number; append?: boolean }) => {
    if (!lead || !emailsEnabled) return;
    const page = options?.page ?? 1;
    const append = options?.append === true;
    if (append) setLeadEmailsLoadingMore(true);
    fetch(`/api/leads/${lead.id}/emails?page=${page}&per_page=25`)
      .then((res) => (res.ok ? res.json() : []))
      .then((payload) => {
        const parsed = parseLeadEmailsPayload(payload);
        setLeadEmails((prev) => (append ? [...prev, ...parsed.items] : parsed.items));
        setLeadEmailsTotal(parsed.total);
        setLeadEmailsPage(parsed.page);
      })
      .catch(() => {
        if (!append) setLeadEmails([]);
      })
      .finally(() => {
        if (append) setLeadEmailsLoadingMore(false);
      });
  }, [lead?.id, emailsEnabled, parseLeadEmailsPayload]);

  React.useEffect(() => {
    if (!lead?.id) return;
    if (emailsEnabled) loadLeadEmails();
    else {
      setLeadEmails([]);
      setLeadEmailsTotal(0);
    }
  }, [lead?.id, emailsEnabled, loadLeadEmails]);

  React.useEffect(() => {
    // Volltext kommt aus dem Posteingang; ohne Recht bleibt das Snippet.
    if (!expandedEmailId || !canViewInbox) return;
    const current = leadEmails.find((e) => e.id === expandedEmailId);
    if (current?.body_html || current?.body_text) return;
    let cancelled = false;
    fetch(`/api/inbox/emails/${encodeURIComponent(expandedEmailId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((row) => {
        if (cancelled || !row || typeof row !== 'object') return;
        const body = row as LeadEmailEntry;
        setLeadEmails((prev) =>
          prev.map((e) =>
            e.id === expandedEmailId
              ? {
                  ...e,
                  body_html: body.body_html ?? e.body_html,
                  body_text: body.body_text ?? e.body_text,
                  attachments_json: body.attachments_json ?? e.attachments_json,
                }
              : e,
          ),
        );
      })
      .catch(() => {
        /* snippet remains as fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [expandedEmailId, leadEmails, canViewInbox]);

  React.useEffect(() => {
    fetch('/api/leads', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        const arr = Array.isArray(list)
          ? list.map((l: Lead & { contact?: LeadOption['contact']; baustelle?: Lead['baustelle'] }) =>
              mapApiLeadToInboxLeadOption(l),
            )
          : [];
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        setLeadsForEmailLink(arr);
      })
      .catch(() => setLeadsForEmailLink([]));
  }, [lead?.id]);

  const loadLeadWithOfferingDraft = React.useCallback(
    async (baseLead: Lead, offeringId: string | null): Promise<Lead> => {
      if (!offeringId || offeringId === baseLead.primary_offering_id) return baseLead;
      try {
        const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}`);
        if (!res.ok) return baseLead;
        const o = (await res.json()) as { offer_document_draft?: Lead['offer_document_draft'] };
        return {
          ...baseLead,
          offer_document_draft: parseOfferDocumentDraft(o.offer_document_draft) ?? null,
        };
      } catch {
        return baseLead;
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!lead) {
      setEditorLead(null);
      return;
    }
    let cancelled = false;
    void loadLeadWithOfferingDraft(lead, editorOfferingId).then((next) => {
      if (!cancelled) setEditorLead(next);
    });
    return () => {
      cancelled = true;
    };
  }, [lead, editorOfferingId, loadLeadWithOfferingDraft]);

  /** Nach PDF-Download/-Versand: Zusammenfassung (Dateien, E-Mails, Aktivit?ten) ohne Hard-Reload. */
  const refreshProjectFeed = React.useCallback(async () => {
    await reloadLead();
    docs.loadDocuments({ force: true });
    activitiesApi.loadActivities();
    loadLeadEmails();
  }, [reloadLead, docs.loadDocuments, activitiesApi.loadActivities, loadLeadEmails]);

  /** Kanonische Projekt-UUID; alte `/offerings/?`-Pfade ? Detail oder Editor mit `?offering=`. */
  React.useEffect(() => {
    if (!lead?.id || !id) return;
    const canonicalId = String(lead.id);

    if (invoiceDocTypeBlocked) {
      navigate(projectDetailPath({ id: canonicalId }), { replace: true });
      return;
    }

    if (offeringIdParam) {
      if (docType && DOCUMENT_SLUG_TO_LABEL[docType]) {
        navigate(projectDocumentEditorPathFromLabel(lead, documentTypeLabel, offeringIdParam), {
          replace: true,
        });
      } else {
        navigate(projectDetailPath({ id: canonicalId }), { replace: true });
      }
      return;
    }

    if (id !== canonicalId) {
      if (docType && DOCUMENT_SLUG_TO_LABEL[docType]) {
        const offeringQ = searchParams.get('offering');
        navigate(
          offeringQ
            ? projectDocumentEditorPathFromLabel({ id: canonicalId }, documentTypeLabel, offeringQ)
            : projectDocumentPathFromSlug({ id: canonicalId }, docType),
          { replace: true },
        );
      } else {
        navigate(projectDetailPath({ id: canonicalId }), { replace: true });
      }
    }
  }, [lead, id, docType, offeringIdParam, documentTypeLabel, searchParams, location.pathname, navigate, invoiceDocTypeBlocked]);

  /** Editor ohne gew?hltes Angebot ? Auswahl-Dialog. */
  React.useEffect(() => {
    if (!offerEditorOpen || !lead || editorOfferingId) return;
    setPendingDocumentTypeLabel(documentTypeLabel);
    setOfferingPickerFromCreateDoc(false);
    setShowOfferingPickerModal(true);
  }, [offerEditorOpen, lead, editorOfferingId, documentTypeLabel]);

  React.useEffect(() => {
    if (!offerEditorOpen || !editorOfferingId) return;
    void markOfferingLastUsed(editorOfferingId);
  }, [offerEditorOpen, editorOfferingId, markOfferingLastUsed]);

  const [showFabMenu, setShowFabMenu] = React.useState(false);
  const [showMobileNoteSheet, setShowMobileNoteSheet] = React.useState(false);

  const [activeMobileSection, setActiveMobileSection] = React.useState<'details' | 'zusammenfassung' | 'angebot' | 'dokumente' | 'kosten'>('zusammenfassung');
  const [showMobileActionsMenu, setShowMobileActionsMenu] = React.useState(false);
  const [isMobileView, setIsMobileView] = React.useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  React.useEffect(() => {
    const handler = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  React.useEffect(() => {
    if (!showCreateDocumentModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowCreateDocumentModal(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showCreateDocumentModal]);

  /** Auf dem Desktop zählt immer die Zusammenfassung; Mobile nutzt die Bereichs-Tabs. */
  const effectiveSection = isMobileView
    ? (activeMobileSection === 'details' ||
      (activeMobileSection === 'kosten' && !rechnungenEnabled) ||
      (activeMobileSection === 'angebot' && !(modulesLoaded && isEnabled('angebote')))
        ? 'zusammenfassung'
        : activeMobileSection)
    : 'zusammenfassung';

  React.useEffect(() => {
    const shouldLoad =
      (effectiveSection === 'dokumente' || effectiveSection === 'zusammenfassung') && lead?.id;
    if (shouldLoad) docs.loadDocuments();
  }, [effectiveSection, lead?.id, docs.loadDocuments]);

  const historyEntries = useLeadHistoryEntries({
    lead,
    activities: activitiesApi.activities,
    noteHistory: notesApi.noteHistory,
    leadEmails,
    documents: docs.documents,
    chats: canViewChats ? leadChats : [],
    tasks: leadTasks.canViewTasks ? leadTasks.tasks : [],
    isLeadLost: leadApi.isLeadLost,
    historyFilter,
  });

  /** Dateien überall auf der Seite ablegen — nicht nur in der Drop-Zone des Dateien-Tabs. */
  const [fileDropFeedback, setFileDropFeedback] = React.useState<LeadFileDropFeedback | null>(null);
  const handlePageFileDrop = React.useCallback(
    async (files: File[]) => {
      if (docs.documentsUploading) return;
      setFileDropFeedback({ kind: 'uploading' });
      const { uploaded, errors } = await docs.uploadFiles(files);
      setFileDropFeedback(() => {
        if (uploaded > 0 && errors.length > 0) {
          return { kind: 'mixed', uploadedCount: uploaded, errors: errors.join(' · ') };
        }
        if (uploaded > 0) return { kind: 'done', count: uploaded };
        if (errors.length > 0) return { kind: 'error', message: errors.join(' · ') };
        return { kind: 'error', message: 'Upload fehlgeschlagen.' };
      });
    },
    [docs.documentsUploading, docs.uploadFiles],
  );
  const fileDropActive = useWindowFileDrop({
    enabled: Boolean(lead?.id) && !offerEditorOpen,
    onFiles: handlePageFileDrop,
    ownDropZoneSelector: '[data-file-drop-zone]',
  });

  if (isLoading) {
    return (
      <LeadDetailShell>
        <PageLoadingState />
      </LeadDetailShell>
    );
  }

  if (!lead) {
    return (
      <LeadDetailShell>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-app-text-muted">Projekt nicht gefunden.</p>
          <Link to={projectsListPath()} className="btn-pipedrive-green inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zu Projekte
          </Link>
        </div>
      </LeadDetailShell>
    );
  }

  const handleDeleteProject = async () => {
    if (!lead?.id) return;
    setIsDeletingProject(true);
    setDeleteProjectError(null);
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(String(lead.id))}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body?.error || 'Projekt konnte nicht gelöscht werden.');
      }
      navigate(projectsListPath());
    } catch (err) {
      console.error('Failed to delete lead', err);
      setDeleteProjectError(
        err instanceof Error ? err.message : 'Projekt konnte nicht gelöscht werden.',
      );
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeletingProject(false);
    }
  };

  const baustelleMapAddress =
    leadApi.siteAddressInput.trim() ||
    (lead.baustelle?.address ?? lead.site_address ?? '').trim() ||
    (lead.contact?.address ?? '').trim();
  const baustelleSiteTrim = (lead.baustelle?.address ?? lead.site_address ?? '').trim();
  const baustelleReadOnlyLine = baustelleSiteTrim || '?';
  const baustelleDescriptionHtml = lead.baustelle?.description ?? lead.notes ?? '';
  const headerContactName = contactDisplayName(lead.contact);
  const headerContactCompany = (lead.contact?.company ?? '').trim();
  const headerCallPhone =
    (lead.baustelle?.contact?.phone ?? '').trim() || (lead.phone ?? '').trim();

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
      <AppSidebar active="leads" />

      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden bg-app-surface-muted/50 pb-16 md:pb-0">
        <AppTopBar title="Projekt" />

        <header className="bg-app-surface md:border-b md:border-app-border z-10 shrink-0 flex flex-col">
          <LeadDetailMobileHero
            lead={lead}
            goBack={goBack}
            showMobileActionsMenu={showMobileActionsMenu}
            setShowMobileActionsMenu={setShowMobileActionsMenu}
            isLeadWon={leadApi.isLeadWon}
            isLeadLost={leadApi.isLeadLost}
            isSaving={leadApi.isSaving}
            handleReopenLead={leadApi.handleReopenLead}
            handleGewonnen={leadApi.handleGewonnen}
            openVerlorenModal={leadApi.openVerlorenModal}
            setShowCreateDocumentModal={setShowCreateDocumentModal}
            baustelleMapAddress={baustelleMapAddress}
            headerContactName={headerContactName}
            headerContactCompany={headerContactCompany}
            headerCallPhone={headerCallPhone}
            terminalStatusLabel={leadApi.terminalStatusLabel}
            status={leadApi.status}
            leadCategories={leadApi.leadCategories}
            handleLeadCategoryChange={leadApi.handleLeadCategoryChange}
            leadTitleEditing={leadApi.leadTitleEditing}
            leadTitleDraft={leadApi.leadTitleDraft}
            setLeadTitleDraft={leadApi.setLeadTitleDraft}
            leadTitleInputMobileRef={leadApi.leadTitleInputMobileRef}
            saveLeadTitleFromEdit={leadApi.saveLeadTitleFromEdit}
            cancelLeadTitleEdit={leadApi.cancelLeadTitleEdit}
            startEditingLeadTitle={leadApi.startEditingLeadTitle}
            offerNetSum={leadApi.offerNetSum}
            projectValue={leadApi.projectValue}
            activeMobileSection={activeMobileSection}
            setActiveMobileSection={setActiveMobileSection}
          />
          <LeadDetailDesktopHeader
            lead={lead}
            goBack={goBack}
            leadApi={leadApi}
            onCreateDocument={() => setShowCreateDocumentModal(true)}
          />
        </header>

        <div className="flex-1 flex min-h-0 overflow-hidden">
          <LeadInfoColumn
            lead={lead}
            leadApi={leadApi}
            instanceMembers={activitiesApi.instanceMembers}
            activeMobileSection={activeMobileSection}
            setActiveMobileSection={setActiveMobileSection}
            baustelleMapAddress={baustelleMapAddress}
            baustelleReadOnlyLine={baustelleReadOnlyLine}
            baustelleDescriptionHtml={baustelleDescriptionHtml}
            onDeleteProject={() => {
              setDeleteProjectError(null);
              setDeleteConfirmOpen(true);
            }}
            onMergeProject={canEditProjects ? () => setMergeOpen(true) : undefined}
            isDeletingProject={isDeletingProject}
            deleteProjectError={deleteProjectError}
          />

          <div
            className={`flex-col min-w-0 min-h-0 overflow-hidden flex-1 ${
              activeMobileSection === 'details' ? 'hidden md:flex' : 'flex'
            }`}
          >
            {effectiveSection === 'zusammenfassung' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-3 sm:gap-4 bg-app-surface-muted/50 px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                  <LeadNoteComposer
                    historyContentTab={historyContentTab}
                    onHistoryContentTabChange={setHistoryContentTab}
                    notesEditorRef={notesApi.notesEditorRef}
                    handleNotesEditorInput={notesApi.handleNotesEditorInput}
                    handleNotesEditorKeyDown={notesApi.handleNotesEditorKeyDown}
                    handleNotesEditorMentionClick={notesApi.handleNotesEditorMentionClick}
                    notesSavedRangeRef={notesApi.notesSavedRangeRef}
                    mentionPickerOpen={notesApi.mentionPickerOpen}
                    mentionAnchorRect={notesApi.mentionAnchorRect}
                    instanceMembers={activitiesApi.instanceMembers}
                    mentionQuery={notesApi.mentionQuery}
                    handleMentionSelect={notesApi.handleMentionSelect}
                    closeMentionPicker={notesApi.closeMentionPicker}
                    linkSavedRangeRef={notesApi.linkSavedRangeRef}
                    showLinkModal={notesApi.showLinkModal}
                    setShowLinkModal={notesApi.setShowLinkModal}
                    linkModalText={notesApi.linkModalText}
                    setLinkModalText={notesApi.setLinkModalText}
                    linkModalUrl={notesApi.linkModalUrl}
                    setLinkModalUrl={notesApi.setLinkModalUrl}
                    showLinkHint={notesApi.showLinkHint}
                    setShowLinkHint={notesApi.setShowLinkHint}
                    openMentionPickerAtCaret={notesApi.openMentionPickerAtCaret}
                    imageSavedRangeRef={notesApi.imageSavedRangeRef}
                    notesImageInputRef={notesApi.notesImageInputRef}
                    noteDraft={notesApi.noteDraft}
                    setNoteDraft={notesApi.setNoteDraft}
                    saveNotes={notesApi.saveNotes}
                    isSaving={notesApi.isSaving}
                    notesSaved={notesApi.notesSaved}
                    activityForm={activitiesApi.activityForm}
                    setActivityForm={activitiesApi.setActivityForm}
                    activityTypesList={activitiesApi.activityTypesList}
                    activityChecklistOptions={activitiesApi.activityChecklistOptions}
                    activityUserSelectOptions={activitiesApi.activityUserSelectOptions}
                    activityEmployeeSelectOptions={activitiesApi.activityEmployeeSelectOptions}
                    activityAddressFieldEl={activitiesApi.activityAddressFieldEl}
                    activityDayCalendarEl={activitiesApi.activityDayCalendarEl}
                    resetActivityForm={activitiesApi.resetActivityForm}
                    addActivity={activitiesApi.addActivity}
                    addingActivity={activitiesApi.addingActivity}
                    activitySaveError={activitiesApi.activitySaveError}
                    historyFileInputRef={docs.historyFileInputRef}
                    handleUploadDocument={docs.handleUploadDocument}
                    documentsUploadError={docs.documentsUploadError}
                    handleDrop={docs.handleDrop}
                    handleDragOver={docs.handleDragOver}
                    handleDragLeave={docs.handleDragLeave}
                    documentsUploading={docs.documentsUploading}
                    isDragging={docs.isDragging}
                    documentsUploadProgress={docs.documentsUploadProgress}
                  />
                  <LeadActivityLists
                    lead={lead}
                    pinnedNotesForLead={notesApi.pinnedNotesForLead}
                    noteHistoryMenuEntryId={notesApi.noteHistoryMenuEntryId}
                    setNoteHistoryMenuEntryId={notesApi.setNoteHistoryMenuEntryId}
                    togglePinnedNoteHistory={notesApi.togglePinnedNoteHistory}
                    deleteNoteHistoryEntry={notesApi.deleteNoteHistoryEntry}
                    activities={activitiesApi.activities}
                    activityTypesList={activitiesApi.activityTypesList}
                    toggleActivityDone={activitiesApi.toggleActivityDone}
                    activityMenuOpen={activitiesApi.activityMenuOpen}
                    setActivityMenuOpen={activitiesApi.setActivityMenuOpen}
                    fillFormFromActivity={activitiesApi.fillFormFromActivity}
                    deleteActivity={activitiesApi.deleteActivity}
                    checklistNameById={activitiesApi.checklistNameById}
                    resetActivityForm={activitiesApi.resetActivityForm}
                    setShowCreateActivityModal={activitiesApi.setShowCreateActivityModal}
                  >
                    <LeadHistoryFeed
                      historyFilter={historyFilter}
                      setHistoryFilter={setHistoryFilter}
                      filterCounts={{
                        activities: activitiesApi.activities.length,
                        notes: notesApi.noteHistory.length,
                        emails: leadEmailsTotal || leadEmails.length,
                        files: docs.documents.length,
                        chats: leadChats.length,
                        tasks: leadTasks.tasks.length,
                      }}
                      emailLinkWarning={emailLinkWarning}
                      setEmailLinkWarning={setEmailLinkWarning}
                      lead={lead}
                      entries={historyEntries}
                      noteHistoryMenuEntryId={notesApi.noteHistoryMenuEntryId}
                      setNoteHistoryMenuEntryId={notesApi.setNoteHistoryMenuEntryId}
                      togglePinnedNoteHistory={notesApi.togglePinnedNoteHistory}
                      deleteNoteHistoryEntry={notesApi.deleteNoteHistoryEntry}
                      activityTypesList={activitiesApi.activityTypesList}
                      expandedEmailId={expandedEmailId}
                      setExpandedEmailId={setExpandedEmailId}
                      canViewInbox={canViewInbox}
                      showEmailsTab={emailsEnabled}
                      showChatsTab={canViewChats}
                      showTasksTab={leadTasks.canViewTasks}
                      canEditTask={leadTasks.canEditTask}
                      taskMembersById={leadTasks.membersById}
                      onToggleTask={leadTasks.toggleDone}
                      leadsForEmailLink={leadsForEmailLink}
                      loadLeadEmails={loadLeadEmails}
                      hasMoreEmails={leadEmails.length < leadEmailsTotal}
                      loadingMoreEmails={leadEmailsLoadingMore}
                      onLoadMoreEmails={() => loadLeadEmails({ page: leadEmailsPage + 1, append: true })}
                      setPreviewDocument={docs.setPreviewDocument}
                      handleDeleteDocument={docs.handleDeleteDocument}
                    />
                  </LeadActivityLists>
                </div>
              </div>
            )}
            {effectiveSection === 'angebot' && id && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <LeadProdukteTab
                  leadRouteId={id}
                  offeringSections={produkteTabSections}
                  onNetSumChange={leadApi.scheduleProjectValueRefresh}
                  onCreateDocument={() => setShowCreateDocumentModal(true)}
                />
              </div>
            )}
            {effectiveSection === 'kosten' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3">
                  <LeadIncomingInvoicesCard leadId={String(lead.id)} />
                  <LeadWorkReportsCard leadId={String(lead.id)} />
                </div>
              </div>
            )}
            {effectiveSection === 'dokumente' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <LeadDocumentsTab
                  leadId={lead.id}
                  documents={docs.documents}
                  documentsLoading={docs.documentsLoading}
                  documentsUploading={docs.documentsUploading}
                  documentsUploadProgress={docs.documentsUploadProgress}
                  documentsUploadError={docs.documentsUploadError}
                  isDragging={docs.isDragging}
                  documentSearch={docs.documentSearch}
                  onDocumentSearchChange={docs.setDocumentSearch}
                  documentSort={docs.documentSort}
                  onToggleSort={docs.toggleSort}
                  selectedDocIds={docs.selectedDocIds}
                  onToggleSelectAll={docs.toggleSelectAll}
                  onToggleSelectDoc={docs.toggleSelectDoc}
                  filteredAndSortedDocuments={docs.filteredAndSortedDocuments}
                  fileInputRef={docs.fileInputRef}
                  cameraInputRef={docs.cameraInputRef}
                  onUploadDocument={docs.handleUploadDocument}
                  onDrop={docs.handleDrop}
                  onDragOver={docs.handleDragOver}
                  onDragLeave={docs.handleDragLeave}
                  onPreview={docs.setPreviewDocument}
                  onDelete={docs.handleDeleteDocument}
                  onCreateDocument={() => setShowCreateDocumentModal(true)}
                />
              </div>
            )}
          </div>
        </div>

        <LeadDetailMobileActions
          activeMobileSection={activeMobileSection}
          fabFileInputRef={docs.fabFileInputRef}
          handleUploadDocument={docs.handleUploadDocument}
          showFabMenu={showFabMenu}
          setShowFabMenu={setShowFabMenu}
          setShowCreateDocumentModal={setShowCreateDocumentModal}
          setMobileNoteContent={setMobileNoteContent}
          setShowMobileNoteSheet={setShowMobileNoteSheet}
          setShowCreateActivityModal={activitiesApi.setShowCreateActivityModal}
          showMobileNoteSheet={showMobileNoteSheet}
          mobileNoteContent={mobileNoteContent}
          saveNotes={notesApi.saveNotes}
          isSaving={notesApi.isSaving}
        />
      </main>

      <LeadFileDropLayer
        dragActive={fileDropActive}
        feedback={fileDropFeedback}
        progress={docs.documentsUploadProgress}
        onDismiss={() => setFileDropFeedback(null)}
      />

      {offerEditorOpen && editorOfferingId && id ? (
        <OfferDocumentEditor
          lead={editorLead ?? lead}
          leadRouteId={id}
          offeringId={editorOfferingId}
          documentTypeLabel={documentTypeLabel}
          onClose={() => navigate(projectDetailPath(lead))}
          onDraftSaved={reloadLead}
          onProjectFeedChanged={refreshProjectFeed}
        />
      ) : null}

      {activitiesApi.activityConflictDialog}
      {invoiceSourceModal}
      <LeadDetailModals
        lead={lead}
        id={id}
        isMobileView={isMobileView}
        showOfferingPickerModal={showOfferingPickerModal}
        setShowOfferingPickerModal={setShowOfferingPickerModal}
        pendingDocumentTypeLabel={pendingDocumentTypeLabel}
        setPendingDocumentTypeLabel={setPendingDocumentTypeLabel}
        documentTypeLabel={documentTypeLabel}
        offeringPickerFromCreateDoc={offeringPickerFromCreateDoc}
        setOfferingPickerFromCreateDoc={setOfferingPickerFromCreateDoc}
        offerEditorOpen={offerEditorOpen}
        navigateToLeadDetail={() => navigate(projectDetailPath(lead))}
        reloadLead={reloadLead}
        openDocumentEditor={openDocumentEditor}
        setShowCreateDocumentModal={setShowCreateDocumentModal}
        showCreateDocumentModal={showCreateDocumentModal}
        previewDocument={docs.previewDocument}
        setPreviewDocument={docs.setPreviewDocument}
        previewZoom={docs.previewZoom}
        setPreviewZoom={docs.setPreviewZoom}
        editingActivity={activitiesApi.editingActivity}
        activitySaveError={activitiesApi.activitySaveError}
        resetActivityForm={activitiesApi.resetActivityForm}
        activityForm={activitiesApi.activityForm}
        setActivityForm={activitiesApi.setActivityForm}
        activityTypesList={activitiesApi.activityTypesList}
        activityChecklistOptions={activitiesApi.activityChecklistOptions}
        activityUserSelectOptions={activitiesApi.activityUserSelectOptions}
        activityEmployeeSelectOptions={activitiesApi.activityEmployeeSelectOptions}
        activityAddressFieldEl={activitiesApi.activityAddressFieldEl}
        activityDayCalendarEl={activitiesApi.activityDayCalendarEl}
        updateActivity={activitiesApi.updateActivity}
        addingActivity={activitiesApi.addingActivity}
        showCreateActivityModal={activitiesApi.showCreateActivityModal}
        setShowCreateActivityModal={activitiesApi.setShowCreateActivityModal}
        addActivity={activitiesApi.addActivity}
        showVerlorenModal={leadApi.showVerlorenModal}
        setShowVerlorenModal={leadApi.setShowVerlorenModal}
        setVerlorenError={leadApi.setVerlorenError}
        handleVerlorenSave={leadApi.handleVerlorenSave}
        isSaving={leadApi.isSaving}
        verlorenError={leadApi.verlorenError}
        categoryChangePending={leadApi.categoryChangePending}
        setCategoryChangePending={leadApi.setCategoryChangePending}
        setCategoryChangeError={leadApi.setCategoryChangeError}
        leadPipelineStages={leadApi.leadPipelineStages}
        leadOutcome={leadApi.leadOutcome}
        categoryChangeError={leadApi.categoryChangeError}
        applyLeadCategoryChange={leadApi.applyLeadCategoryChange}
      />

      <AppConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !isDeletingProject && setDeleteConfirmOpen(false)}
        onConfirm={() => void handleDeleteProject()}
        title="Projekt löschen?"
        description={`„${lead.lead_title || lead.display_id || 'Projekt'}“ wird in den Papierkorb verschoben und ist dort 28 Tage lang wiederherstellbar.`}
        confirmLabel="Löschen"
        destructive
        isLoading={isDeletingProject}
      />

      <MergeProjectModal
        open={mergeOpen}
        source={lead}
        onClose={() => setMergeOpen(false)}
        onMerged={(result) => {
          setMergeOpen(false);
          navigate(projectDetailPath({ id: result.target_id }));
        }}
      />
    </div>
  );
}
